'use client';

import { useIntl } from 'react-intl';
import React, { useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { RatingStar } from '@packages/shared/src/ui/Rating/RatingStar';
import { getStarFillAmount } from '@packages/shared/src/ui/Rating/getStarFillAmount';
import { FeedbackNotification } from '@packages/shared/src/ui/Notification/FeedbackNotification';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import type { CreateReviewFormSchema } from '../AddReviewFormValidation/AddReviewFormValidation';
import { ratingParser } from '../AddReviewFormValidation/AddReviewFormValidation';
import { ratingInputMessages } from './RatingInput.messages';

const getMessageDescriptor = (value: number) =>
  ratingInputMessages[`rating${value}` as keyof typeof ratingInputMessages];

/**
 * component with rating icons to select a rating from 1 to 5
 */

export const RatingInput = () => {
  const { formatMessage } = useIntl();
  const {
    customerRatings: { maxRating },
  } = useConfig();

  const {
    formState: { errors },
    control,
  } = useFormContext<CreateReviewFormSchema>();
  const {
    field: { onChange, value },
  } = useController({
    control,
    name: 'rating',
  });
  const [hover, setHover] = useState(0);

  let headlineMessageDescriptor = ratingInputMessages.ratingDefault;
  if (hover > 0 || (value && value > 0)) {
    headlineMessageDescriptor = getMessageDescriptor(hover > 0 ? hover : (value ?? 0));
  }

  const ratingStarClickHandler = (ratingValue: number) => {
    // validate value and set
    ratingParser.safeParse(ratingValue).success && onChange(ratingValue);
  };
  const errorId = React.useId();

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="text-size-headline-base">{formatMessage(headlineMessageDescriptor)}</div>
      <div
        className="flex gap-1"
        onMouseLeave={() => setHover(0)}
        aria-errormessage={errorId}
        aria-describedby={errorId}
        aria-invalid={errors.rating ? 'true' : undefined}
      >
        {Array.from({ length: maxRating }, (_, index) => index + 1).map((star) => (
          <RatingStar
            key={star}
            role="button"
            tabIndex={0}
            fillPercentage={getStarFillAmount(star, hover || (value ?? 0))}
            className="size-4 cursor-pointer [&_span]:size-full"
            aria-label={formatMessage(getMessageDescriptor(star))}
            title={formatMessage(getMessageDescriptor(star))}
            onClick={() => {
              ratingStarClickHandler(star);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                ratingStarClickHandler(star);
              }
            }}
            onMouseEnter={() => setHover(star)}
          />
        ))}
      </div>
      <div className="text-size-body-sm text-color-text-darktransparent">
        {formatMessage(ratingInputMessages.inputHint)}
      </div>
      {errors.rating && (
        <FeedbackNotification contentId={errorId} severity="error" className="mt-0.5">
          {formatMessage(ratingInputMessages.error)}
        </FeedbackNotification>
      )}
    </div>
  );
};
